import { Injectable } from '@angular/core';
import { Page } from '@app/models/api/page.model';
import { PagingParams } from '@app/models/api/paging-params.model';
import { SortingParams } from '@app/models/api/sorting-params.model';
import { DateUtils } from '@app/shared/utils/date.utils';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { QualificationAPIService } from '../api/qualification-api.service';
import { QualificationMessages } from '../locale/qualification.messages';
import { CreateQualificationDto } from '../model/create-qualification.model';
import { Qualification } from '../model/qualification.model';
import { QualificationSidebarData } from '../model/qualification-sidebar-data.model';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

@Injectable({
  providedIn: 'root'
})
export class QualificationBusinessService {

  // key -> the write currently in flight for that qualification. A duplicate call while one is in
  // flight is coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  private _updating: InFlightRegistry;
  uploadingFile: boolean;

  constructor(
    private qualificationAPI: QualificationAPIService,
    private notifyUtils: NotifyUtils
  ) {
    this._updating = new InFlightRegistry();
    this.uploadingFile = false;
  }

  get updating(): number[] {
    return this._updating.keys<number>();
  }

  private sortQualifications(qualifications: Qualification[]): Qualification[] {
    return qualifications.sort((a, b) => {
      let timeA = 0;
      if (a.receivedDate) {
        timeA = new Date(a.receivedDate).getTime();
      }
      let timeB = 0;
      if (b.receivedDate) {
        timeB = new Date(b.receivedDate).getTime();
      }
      return timeB - timeA;
    });
  }

  get(
    ids: number[],
    name: string,
    creatorIds: number[]
  ): Observable<Qualification[]> {
    return this.qualificationAPI
      .get(ids, name, creatorIds)
      .pipe(
        map(qualifications => this.sortQualifications(qualifications))
      );
  }

  getPaginated(
    pagingParams: PagingParams,
    sortingParams: SortingParams,
    ids: number[],
    name: string,
    creatorIds: number[]
  ): Observable<Page<Qualification>> {
    return this.qualificationAPI.getPaginated(pagingParams, sortingParams, ids, name, creatorIds);
  }

  create(createDto: CreateQualificationDto): Promise<Qualification> {
    createDto.receivedDate = DateUtils.setTimezoneToUTCButKeepDate(createDto.receivedDate);

    return firstValueFrom(this.qualificationAPI.create(createDto))
      .then(res => {
        this.notifyUtils.notify(QualificationMessages.SUCCESS_CREATE);
        return res;
      });
  }

  update(id: number, updateDto: CreateQualificationDto): Promise<Qualification> {
    updateDto.receivedDate = DateUtils.setTimezoneToUTCButKeepDate(updateDto.receivedDate);

    return this._updating.run(id, () =>
      firstValueFrom(this.qualificationAPI.update(id, updateDto))
        .then(res => {
          this.notifyUtils.notify(QualificationMessages.SUCCESS_UPDATE);
          return res;
        })
    );
  }

  delete(id: number): Promise<Qualification> {
    return this._updating.run(id, () =>
      firstValueFrom(this.qualificationAPI.delete(id))
        .then(res => {
          this.notifyUtils.notify(QualificationMessages.SUCCESS_DELETE);
          return res;
        })
    );
  }
  
  getSidebarData(userIds: number[]): Observable<QualificationSidebarData[]> {
    return this.qualificationAPI.getSidebarData(userIds);
  }

}
